import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';
import {
    ChartBarIcon,
    DocumentTextIcon,
    CreditCardIcon,
    ClockIcon,
    ArrowLeftIcon,
    ArrowRightIcon,
    ArrowLeftCircleIcon
} from '@heroicons/react/24/outline';
import RemittanceDetailHeader from './detail/RemittanceDetailHeader';
import WeekOverWeekComparison from './detail/WeekOverWeekComparison';
import RemittanceFinancialCharts from './detail/RemittanceFinancialCharts';
import CarFinancialDetailCards from './detail/CarFinancialDetailCards';
import DetailedExpenseBreakdown from './detail/DetailedExpenseBreakdown';
import EnhancedJobCardDetails from './detail/EnhancedJobCardDetails';
import PaymentAuditTrail from './detail/PaymentAuditTrail';
import RemittanceStatusTimeline from './detail/RemittanceStatusTimeline';

const OwnerRemittanceDetail = () => {
    const { owner_code, week_key } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();

    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Data states
    const [ownerReport, setOwnerReport] = useState(null);
    const [weekComparison, setWeekComparison] = useState(null);
    const [expenseBreakdown, setExpenseBreakdown] = useState(null);
    const [jobCards, setJobCards] = useState(null);
    const [paymentAudit, setPaymentAudit] = useState(null);
    const [ownerTrend, setOwnerTrend] = useState(null);

    const fetchAllData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const headers = {
                Authorization: `Bearer ${token}`
            };

            // Fetch all data in parallel
            const [
                reportRes,
                comparisonRes,
                expenseRes,
                jobCardsRes,
                paymentRes,
                trendRes
            ] = await Promise.all([
                axios.get(`${rootUrl}/api/remittance/owner-report/${owner_code}/${week_key}`, { headers }),
                axios.get(`${rootUrl}/api/remittance/owner-comparison/${owner_code}/${week_key}`, { headers }),
                axios.get(`${rootUrl}/api/remittance/expenses/detailed/${owner_code}/${week_key}`, { headers }),
                axios.get(`${rootUrl}/api/remittance/owner/${owner_code}/${week_key}/job-cards`, { headers }),
                axios.get(`${rootUrl}/api/remittance/payment-audit/${owner_code}/${week_key}`, { headers }),
                axios.get(`${rootUrl}/api/remittance/owner-trend/${owner_code}?weeks=8`, { headers })
            ]);

            setOwnerReport(reportRes.data.data);
            setWeekComparison(comparisonRes.data.data);
            setExpenseBreakdown(expenseRes.data.data);
            setJobCards(jobCardsRes.data.data);
            setPaymentAudit(paymentRes.data.data);
            setOwnerTrend(trendRes.data.data);

        } catch (err) {
            console.error('Error fetching remittance detail data:', err);
            setError(err.response?.data?.message || 'Failed to load remittance details');
        } finally {
            setLoading(false);
        }
    }, [token, owner_code, week_key]);

    useEffect(() => {
        if (owner_code && week_key) {
            fetchAllData();
        }
    }, [owner_code, week_key, fetchAllData]);

    const handleWeekNavigation = (direction) => {
        // TODO: Implement week navigation
        console.log(`Navigate to ${direction} week`);
    };

    const handleBackToList = () => {
        navigate('/remittance/summary-report');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#FF5733] mx-auto mb-4"></div>
                    <p className="text-lg text-[#2a2e30]">Loading remittance details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
                <div className="p-6 lg:p-8">
                    <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h2 className="text-2xl font-bold text-[#2a2e30] mb-2">Error Loading Data</h2>
                        <p className="text-lg text-gray-600 mb-6">{error}</p>
                        <button
                            onClick={handleBackToList}
                            className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-6 py-2 shadow-none"
                        >
                            Back to Remittance List
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            {/* Page Header */}
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">
                        Owner Remittance Detail Report
                    </h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Comprehensive analysis for {ownerReport?.owner_name || owner_code} - Week {week_key}
                    </p>
                </div>

                {/* Navigation Breadcrumbs */}
                <div className="max-w-7xl mx-auto mb-6">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={handleBackToList}
                            className="flex items-center gap-2 text-[#2a2e30] hover:text-[#FF5733] transition-colors"
                        >
                            <ArrowLeftCircleIcon className="w-6 h-6" />
                            <span className="font-semibold">Back to List</span>
                        </button>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleWeekNavigation('prev')}
                                className="p-2 rounded-sm bg-slate-100 hover:bg-slate-200 shadow-none"
                                title="Previous Week"
                            >
                                <ArrowLeftIcon className="w-5 h-5" />
                            </button>
                            <span className="px-4 py-2 bg-white rounded-sm font-semibold">
                                Week {week_key}
                            </span>
                            <button
                                onClick={() => handleWeekNavigation('next')}
                                className="p-2 rounded-sm bg-slate-100 hover:bg-slate-200 shadow-none"
                                title="Next Week"
                            >
                                <ArrowRightIcon className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                {/* Header Summary */}
                {ownerReport && (
                    <RemittanceDetailHeader
                        ownerReport={ownerReport}
                        ownerCode={owner_code}
                        weekKey={week_key}
                    />
                )}

                {/* Tab Strip */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="flex border-b border-gray-200 overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 whitespace-nowrap ${activeTab === 'overview'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="md:hidden">📊</span>
                            <span className="hidden md:inline">Overview</span>
                        </button>

                        <button
                            onClick={() => setActiveTab('comparison')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 whitespace-nowrap ${activeTab === 'comparison'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="md:hidden">📈</span>
                            <span className="hidden md:inline">Week Comparison</span>
                        </button>

                        <button
                            onClick={() => setActiveTab('charts')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 whitespace-nowrap ${activeTab === 'charts'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="md:hidden">
                                <ChartBarIcon className="w-5 h-5 inline" />
                            </span>
                            <span className="hidden md:inline">Financial Charts</span>
                        </button>

                        <button
                            onClick={() => setActiveTab('expenses')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 whitespace-nowrap ${activeTab === 'expenses'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="md:hidden">
                                <DocumentTextIcon className="w-5 h-5 inline" />
                            </span>
                            <span className="hidden md:inline">Expenses</span>
                        </button>

                        <button
                            onClick={() => setActiveTab('jobcards')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 whitespace-nowrap ${activeTab === 'jobcards'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="md:hidden">🔧</span>
                            <span className="hidden md:inline">Job Cards</span>
                        </button>

                        <button
                            onClick={() => setActiveTab('payments')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 whitespace-nowrap ${activeTab === 'payments'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="md:hidden">
                                <CreditCardIcon className="w-5 h-5 inline" />
                            </span>
                            <span className="hidden md:inline">Payment Audit</span>
                        </button>

                        <button
                            onClick={() => setActiveTab('timeline')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 whitespace-nowrap ${activeTab === 'timeline'
                                ? 'border-[#FF5733] text-[#FF5733]'
                                : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            <span className="md:hidden">
                                <ClockIcon className="w-5 h-5 inline" />
                            </span>
                            <span className="hidden md:inline">Timeline</span>
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div className="p-6">
                        {activeTab === 'overview' && ownerReport && (
                            <CarFinancialDetailCards cars={ownerReport.cars} />
                        )}

                        {activeTab === 'comparison' && weekComparison && (
                            <WeekOverWeekComparison comparisonData={weekComparison} />
                        )}

                        {activeTab === 'charts' && ownerReport && ownerTrend && (
                            <RemittanceFinancialCharts
                                ownerReport={ownerReport}
                                trendData={ownerTrend}
                                expenseBreakdown={expenseBreakdown}
                            />
                        )}

                        {activeTab === 'expenses' && expenseBreakdown && (
                            <DetailedExpenseBreakdown breakdownData={expenseBreakdown} />
                        )}

                        {activeTab === 'jobcards' && jobCards && (
                            <EnhancedJobCardDetails jobCardsData={jobCards} />
                        )}

                        {activeTab === 'payments' && paymentAudit && (
                            <PaymentAuditTrail auditData={paymentAudit} />
                        )}

                        {activeTab === 'timeline' && ownerReport && (
                            <RemittanceStatusTimeline
                                cars={ownerReport.cars}
                                token={token}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OwnerRemittanceDetail;
